import { useEffect, useRef, useState } from 'react'
import cx from 'classnames'
import { CreditCard, LogOut, Moon, Settings, User } from 'lucide-react'
import { Link } from 'react-router'

import useCurrentAccount from 'query/useCurrentAccount'
import useCurrentUser from 'query/useCurrentUser'
import { Avatar, Icon } from 'ui'
import { useTheme } from 'utils/theme'
import { isAdmin, isPaidAccount } from 'utils/validation'

const getInitials = currentUser => {
  const firstName = currentUser.firstName || currentUser.name || currentUser.email || ''
  const lastName = currentUser.lastName || ''

  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U'
}

const UserBox = () => {
  const { data: currentUser = {} } = useCurrentUser()
  const { data: currentAccount = {} } = useCurrentAccount({
    enabled: isAdmin(currentUser),
  })

  const { isDark, toggleTheme } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const menuRef = useRef(null)
  const triggerRef = useRef(null)

  useEffect(() => {
    if (!isOpen) {
      return undefined
    }

    const closeOnOutsideClick = event => {
      if (!menuRef.current?.contains(event.target)) {
        setIsOpen(false)
      }
    }

    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setIsOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)

    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [isOpen])

  if (!currentUser.email && !currentUser.firstName && !currentUser.name) {
    return null
  }

  const displayName = `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim()
    || currentUser.name
    || currentUser.email
    || 'User'

  const menuItems = [
    { icon: User, label: 'Profile', to: '/profile' },
    ...(isPaidAccount(currentAccount) ? [{ icon: Settings, label: 'Account Settings', to: '/settings' }] : []),
    ...(isAdmin(currentUser) ? [{ icon: CreditCard, label: 'Billing', to: '/billing' }] : []),
  ]

  return (
    <div className="tw:relative" ref={menuRef}>
      <button
        aria-expanded={isOpen}
        aria-label={`Account menu — ${displayName}`}
        className="ds-avatar-button tw:flex tw:items-center tw:gap-2 tw:rounded-button tw:border-0 tw:bg-transparent tw:p-0"
        onClick={() => setIsOpen(open => !open)}
        ref={triggerRef}
        type="button"
      >
        <span className="tw:sr-only">{displayName}</span>
        <Avatar label={getInitials(currentUser)} tone="you" />
      </button>

      {isOpen && (
        <div className="ds-menu-enter tw:absolute tw:right-0 tw:top-[calc(100%+12px)] tw:z-50 tw:w-68 tw:overflow-hidden tw:rounded-card tw:border tw:border-default tw:bg-surface-raised tw:p-2 tw:shadow-md">
          <div className="tw:flex tw:items-center tw:gap-3 tw:px-3 tw:py-3">
            <Avatar label={getInitials(currentUser)} size="sm" tone="you" />
            <span className="tw:min-w-0">
              <span className="tw:block tw:truncate tw:text-sm tw:font-semibold tw:text-foreground-strong">{displayName}</span>
              <span className="tw:block tw:truncate tw:text-sm tw:text-foreground-subtle">{currentUser.email}</span>
            </span>
          </div>
          <div className="tw:my-1 tw:border-t tw:border-default" />
          {menuItems.map(item => (
            <Link
              className="tw:flex tw:min-h-10 tw:items-center tw:gap-2 tw:rounded-sm tw:px-3 tw:text-sm tw:text-foreground-muted tw:no-underline tw:hover:bg-surface-hover tw:hover:text-foreground-strong tw:hover:no-underline"
              key={item.to}
              onClick={() => setIsOpen(false)}
              to={item.to}
            >
              <Icon as={item.icon} size={17} />
              {item.label}
            </Link>
          ))}
          <div className="tw:my-1 tw:border-t tw:border-default" />
          <div className="tw:flex tw:min-h-12 tw:items-center tw:justify-between tw:gap-3 tw:rounded-sm tw:px-3">
            <span className="tw:flex tw:items-center tw:gap-2 tw:text-sm tw:text-foreground-muted">
              <Icon as={Moon} size={17} />
              Dark Mode
            </span>
            <button
              aria-checked={isDark}
              aria-label="Dark Mode"
              className={cx(
                'tw:flex tw:h-6 tw:w-11 tw:shrink-0 tw:items-center tw:rounded-button tw:border tw:p-[3px] tw:transition-[background-color,border-color] tw:duration-fast tw:ease-standard',
                isDark ? 'tw:border-brand-red-500 tw:bg-brand-red-500' : 'tw:border-strong tw:bg-surface-subtle',
              )}
              onClick={toggleTheme}
              role="switch"
              type="button"
            >
              <span
                className={cx(
                  'tw:block tw:h-4 tw:w-4 tw:shrink-0 tw:rounded-full tw:transition-[background-color,transform] tw:duration-base tw:ease-out-quart',
                  isDark ? 'tw:translate-x-5 tw:bg-white' : 'tw:translate-x-0 tw:bg-foreground-strong',
                )}
              />
            </button>
          </div>
          <div className="tw:my-1 tw:border-t tw:border-default" />
          <button
            className="tw:flex tw:min-h-10 tw:w-full tw:items-center tw:gap-2 tw:rounded-sm tw:border-0 tw:bg-transparent tw:px-3 tw:text-left tw:text-sm tw:text-foreground-muted tw:hover:bg-surface-hover tw:hover:text-foreground-strong"
            onClick={() => window.location.assign('/users/sign_out')}
            type="button"
          >
            <Icon as={LogOut} size={17} />
            Sign Out
          </button>
        </div>
      )}
    </div>
  )
}

export default UserBox
